import { type RefObject, useCallback } from 'react';
import { type Object3D, Vector3 } from 'three';

import type { PlayerCameraControlsHandle } from '@cartwhl/3d/player';

import { CHARACTER_ROTATION } from '../../constants';
import { resolveKeyPoseFkJointObject } from '../fk/keyPoseFkConfig';
import { useCurrentFrameKeyPoseEditing } from '../hooks/useCurrentFrameKeyPoseEditing';
import { useKeyPoseHotkeys } from '../hooks/useKeyPoseHotkeys';
import { resolveKeyPoseIkEffectorObject } from '../ik/keyPoseIkConfig';
import { createEditableKeyPoseScene } from '../ik/keyPoseIkSystem';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import type { KeyPoseEditingState, KeyPoseRecord } from '../types';

interface KeyPoseHotkeysBindingProps {
  cameraControlsRef: RefObject<PlayerCameraControlsHandle | null>;
}

export default function KeyPoseHotkeysBinding({
  cameraControlsRef,
}: KeyPoseHotkeysBindingProps) {
  const keyPoseStore = useKeyPoseStore();
  const { actorScene, keyPose } = useCurrentFrameKeyPoseEditing({
    sceneRotation: CHARACTER_ROTATION,
  });
  const focusSelectedJoint = useCallback(() => {
    focusCameraOnKeyPoseSelection({
      actorScene,
      cameraControls: cameraControlsRef.current,
      editing: keyPoseStore.state.editing,
      keyPose,
    });
  }, [actorScene, cameraControlsRef, keyPose, keyPoseStore]);

  useKeyPoseHotkeys({
    enabled: true,
    onFocusSelectedJoint: focusSelectedJoint,
  });

  return null;
}

function focusCameraOnKeyPoseSelection({
  actorScene,
  cameraControls,
  editing,
  keyPose,
}: {
  actorScene: Object3D | null;
  cameraControls: PlayerCameraControlsHandle | null;
  editing: KeyPoseEditingState;
  keyPose: KeyPoseRecord | null;
}) {
  if (!cameraControls || !keyPose) {
    return false;
  }

  const scene = createEditableKeyPoseScene({
    actorScene,
    keyPose,
    sceneRotation: CHARACTER_ROTATION,
  });
  if (!scene) {
    return false;
  }

  const targetObject = resolveKeyPoseSelectedFocusObject(scene, editing);
  if (!targetObject) {
    return false;
  }

  return focusCameraOnObject(cameraControls, targetObject);
}

function resolveKeyPoseSelectedFocusObject(
  scene: Object3D,
  editing: KeyPoseEditingState,
) {
  if (editing.mode === 'ik') {
    return editing.selectedIkEffectorId
      ? resolveKeyPoseIkEffectorObject(scene, editing.selectedIkEffectorId)
      : null;
  }

  return editing.selectedFkJointId
    ? resolveKeyPoseFkJointObject(scene, editing.selectedFkJointId)
    : null;
}

function focusCameraOnObject(
  cameraControls: PlayerCameraControlsHandle,
  object: Object3D,
) {
  object.updateWorldMatrix(true, false);

  const nextTarget = object.getWorldPosition(new Vector3());
  if (!isFiniteVector3(nextTarget)) {
    return false;
  }

  const currentPosition = cameraControls.getPosition(new Vector3());
  const currentTarget = cameraControls.getTarget(new Vector3());
  const offset = currentPosition.sub(currentTarget);
  if (!isFiniteVector3(offset) || offset.lengthSq() === 0) {
    offset.set(0, 0, 3);
  }

  const nextPosition = nextTarget.clone().add(offset);
  if (!isFiniteVector3(nextPosition)) {
    return false;
  }

  cameraControls.setLookAt(
    nextPosition.x,
    nextPosition.y,
    nextPosition.z,
    nextTarget.x,
    nextTarget.y,
    nextTarget.z,
    true,
  );

  return true;
}

function isFiniteVector3(vector: Vector3) {
  return (
    Number.isFinite(vector.x) &&
    Number.isFinite(vector.y) &&
    Number.isFinite(vector.z)
  );
}
